import { useEffect, useMemo, useState } from "react";
import { nameProblem, autoFixName } from "@/lib/names";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { localeFor } from "@/lib/money";
import IncentiveCountries from "@/components/IncentiveCountries";
import DeleteRequests from "@/components/DeleteRequests";
import SkillRequests from "@/components/SkillRequests";
import LoginHistory from "@/components/LoginHistory";
import GhlNoshowRuns from "@/components/GhlNoshowRuns";
import { atLeast } from "@/lib/roles";
import {
  globalAdminOverview, globalAdminRevenue, globalAdminUsers, createFacilityWithAdmin,
  updateFacility, deactivateFacility, deleteFacilityPermanent,
  inviteUser, updateUserRole, deleteUser, attachUserToFacility, detachUserFromFacility,
  listUsers, listInvites, cancelInvite,
  setUserQcAccess, setUserLeadNurtureAccess, setUserNurtureManager, setUserAccountsAccess, setUserAccountsHead, listBackups, runBackupNow, API, adminDeleteRequestsCount, skillRequestsCount, listStartedUnpaid,
} from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck, Building2, Users as UsersIcon, Plus, Trash2, Power, PowerOff,
  Crown, Shield, Globe2, ChevronRight, Pencil, X, UserPlus, Link2, Unlink, Mail, Check,
  Database, Download, Percent, Eraser, LogIn, CalendarX, BadgeCheck } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";

const COUNTRY_PRESETS = [
  { country: "India",     currency_code: "INR", currency_symbol: "\u20B9", timezone: "Asia/Kolkata" },
  { country: "UAE",       currency_code: "AED", currency_symbol: "AED",    timezone: "Asia/Dubai" },
  { country: "Sri Lanka", currency_code: "LKR", currency_symbol: "Rs",     timezone: "Asia/Colombo" },
  { country: "USA",       currency_code: "USD", currency_symbol: "$",      timezone: "America/New_York" },
  { country: "UK",        currency_code: "GBP", currency_symbol: "\u00A3", timezone: "Europe/London" },
];

// Small local formatters for the "Last updated" column so we don't pull moment/dayjs just for this.
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const fmtRelative = (iso) => {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "—";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};

export default function GlobalAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");   // ?tab=skills from the approval email
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delWaiting, setDelWaiting] = useState(0);
  useEffect(() => { adminDeleteRequestsCount().then((d) => setDelWaiting(d.waiting || 0)).catch(() => {}); }, []);
  const isMaster = atLeast(user?.role, "master_admin");     // only a master admin approves PT / SPT skills
  const [skillWaiting, setSkillWaiting] = useState(0);
  useEffect(() => { if (isMaster) skillRequestsCount().then((d) => setSkillWaiting(d.waiting || 0)).catch(() => {}); }, [isMaster]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [ov, us, inv] = await Promise.all([
        globalAdminOverview(),
        globalAdminUsers(),
        listInvites("all").catch(() => []),
      ]);
      setOverview(ov);
      setUsers(us);
      setInvites(inv);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load Global Admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-8" data-testid="global-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-amber-400" />
            {user?.role === "master_admin" ? "Master Admin Console" : "Admin Console"}
          </div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Global Admin</h2>
          <p className="text-sm text-zinc-400 mt-1">
            {user?.role === "master_admin"
              ? "Manage every facility and every user across the whole company. "
              : "Assign staff across facilities and manage cross-tenant teams. Facility creation & role promotion stays with Master Admin. "}
            Signed in as
            <span className="text-amber-300 font-medium ml-1">{user?.email}</span>.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0f0f0f] border border-[#262626]">
          <TabsTrigger value="overview" data-testid="global-tab-overview">
            <Globe2 className="h-3.5 w-3.5 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="facilities" data-testid="global-tab-facilities">
            <Building2 className="h-3.5 w-3.5 mr-1.5" /> Facilities
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="global-tab-users">
            <UsersIcon className="h-3.5 w-3.5 mr-1.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="incentives" data-testid="global-tab-incentives">
            <Percent className="h-3.5 w-3.5 mr-1.5" /> Incentives
          </TabsTrigger>
          <TabsTrigger value="deletions" data-testid="global-tab-deletions">
            <Eraser className="h-3.5 w-3.5 mr-1.5" /> Deletion requests
            {delWaiting > 0 && <span className="ml-1.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] px-1.5" data-testid="global-deletions-waiting">{delWaiting}</span>}
          </TabsTrigger>
          {isMaster && (
            <TabsTrigger value="skills" data-testid="global-tab-skills">
              <BadgeCheck className="h-3.5 w-3.5 mr-1.5" /> Skill approvals
              {skillWaiting > 0 && <span className="ml-1.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] px-1.5" data-testid="global-skills-waiting">{skillWaiting}</span>}
            </TabsTrigger>
          )}
          <TabsTrigger value="logins" data-testid="global-tab-logins">
            <LogIn className="h-3.5 w-3.5 mr-1.5" /> Logins
          </TabsTrigger>
          <TabsTrigger value="noshows" data-testid="global-tab-noshows">
            <CalendarX className="h-3.5 w-3.5 mr-1.5" /> Auto no-shows
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab overview={overview} loading={loading} onGoto={setTab} />
        </TabsContent>

        <TabsContent value="facilities" className="mt-6">
          <FacilitiesTab
            overview={overview}
            users={users}
            loading={loading}
            onRefresh={refresh}
          />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <UsersTab
            users={users}
            invites={invites}
            facilities={overview?.facilities || []}
            currentUserId={user?.user_id}
            currentRole={user?.role}
            onRefresh={refresh}
          />
        </TabsContent>

        <TabsContent value="incentives" className="mt-6 space-y-4">
          <p className="text-sm text-zinc-400 max-w-3xl">
            Incentives are set per country and apply to every gym in it, in that country&apos;s currency. Each gym&apos;s
            Settings shows them read-only. Lead nurture slabs are on the Lead Nurture dashboard (Script tab).
          </p>
          <IncentiveCountries fields={["sales", "sales_bonus", "fe_conversion", "nutrition_rate", "blood_test_cost"]} canEdit={atLeast(user?.role, "master_admin")} />
        </TabsContent>

        <TabsContent value="deletions" className="mt-6">
          <DeleteRequests onCount={setDelWaiting} />
        </TabsContent>

        {isMaster && (
          <TabsContent value="skills" className="mt-6">
            <SkillRequests onCount={setSkillWaiting} />
          </TabsContent>
        )}

        <TabsContent value="logins" className="mt-6">
          <LoginHistory />
        </TabsContent>

        <TabsContent value="noshows" className="mt-6">
          <GhlNoshowRuns />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------- Overview ------------------------------- */
// Nightly database backups (Master Admin only). Files live on the server's volume.
function BackupsCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const isMaster = user?.role === "master_admin";
  const load = () => listBackups().then(setData).catch(() => setData(null));
  useEffect(() => { if (isMaster) load(); }, [isMaster]);
  if (!isMaster || !data) return null;
  const backups = data.backups || [];
  const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  const backupNow = async () => {
    setBusy(true);
    try {
      const r = await runBackupNow();
      toast.success(`Backup saved: ${r.documents} records`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-5" data-testid="global-backups">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-blue-400" />
          <div>
            <div className="eyebrow">Database backups</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {data.enabled ? `Every night at 2:00 AM IST · last ${data.keep} kept` : "Not configured on this server"}
            </div>
          </div>
        </div>
        {data.enabled && (
          <Button size="sm" variant="outline" onClick={backupNow} disabled={busy} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="backup-now-btn">
            {busy ? "Backing up…" : "Back up now"}
          </Button>
        )}
      </div>
      {backups.length > 0 ? (
        <div className="divide-y divide-[#1a1a1a] text-sm">
          {backups.map((b) => (
            <div key={b.name} className="flex items-center justify-between py-2">
              <div>
                <div className="text-zinc-200">{new Date(b.created_at).toLocaleString()}</div>
                <div className="text-[11px] text-zinc-500">{fmtSize(b.size_bytes)}</div>
              </div>
              <a href={`${API}/admin/backups/${b.name}`} className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1" data-testid={`backup-download-${b.name}`}>
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            </div>
          ))}
        </div>
      ) : data.enabled && <div className="text-xs text-zinc-500">No backups yet.</div>}
    </section>
  );
}

// Revenue per gym for a month (CR-0032). Same figures as Accounts and each gym's Dashboard;
// every gym in its own currency, totals per currency (never converted).
function RevenueByGym() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null); setErr("");
    globalAdminRevenue(month).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load revenue"));
  }, [month]);
  const money = (row, n) => {
    const sym = (row.currency_symbol && String(row.currency_symbol).trim()) ? row.currency_symbol : `${row.currency_code} `;
    return `${sym}${new Intl.NumberFormat(localeFor(row.currency_code), { maximumFractionDigits: 0 }).format(Math.round(n || 0))}`;
  };
  return (
    <section className="card-surface overflow-hidden" data-testid="global-revenue">
      <div className="flex items-end justify-between gap-3 flex-wrap px-5 pt-5">
        <div>
          <div className="eyebrow">Revenue by gym</div>
          <div className="text-[11px] text-zinc-500 mt-1">Each gym in its own currency. Total = collected incl. tax, minus refunds (as each gym&apos;s Dashboard). Net = after tax and payment fees.</div>
        </div>
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
          className="w-44 bg-[#0a0a0a] border-[#262626] [color-scheme:dark]" data-testid="global-revenue-month" />
      </div>
      {err ? <div className="px-5 py-6 text-sm text-rose-300">{err}</div> : !data ? <div className="px-5 py-6 text-sm text-zinc-500">Loading…</div> : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-y border-[#1f1f1f] bg-[#0d0d0d]">
                <th className="px-5 py-2.5 font-medium">Gym</th>
                <th className="px-3 py-2.5 font-medium text-right">Sales</th>
                <th className="px-3 py-2.5 font-medium text-right">Total revenue</th>
                <th className="px-3 py-2.5 font-medium text-right">Net revenue</th>
                <th className="px-3 py-2.5 font-medium text-right">Pending payments</th>
                <th className="px-5 py-2.5 font-medium text-right" title="Programmes started this month (nutrition consult done) before being paid in full, with or without the facility head's approval">Started without full payment</th>
              </tr>
            </thead>
            <tbody>
              {data.gyms.map((g) => (
                <tr key={g.facility_id} className="border-b border-[#1a1a1a]" data-testid={`global-revenue-${g.facility_id}`}>
                  <td className="px-5 py-2.5">{g.gym}<span className="text-[11px] text-zinc-500 ml-2">{g.currency_code}</span></td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-zinc-400">{g.sales_count}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{money(g, g.total_revenue)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{money(g, g.net_revenue)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-amber-300">{money(g, g.pending)}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums" data-testid={`global-started-unpaid-${g.facility_id}`}
                    title={(g.started_unpaid || []).map((u) => `${u.name} (${u.programme || "—"}): ${u.paid_pct != null ? `${u.paid_pct}% paid, ` : ""}owed ${Math.round(u.owed || 0)}, approved by ${u.by || "—"}: ${u.reason || ""}`).join("\n")}>
                    {(g.started_unpaid || []).length ? <span className="text-rose-300 font-semibold">{g.started_unpaid.length}</span> : <span className="text-zinc-600">0</span>}
                  </td>
                </tr>
              ))}
              {data.totals.map((t) => (
                <tr key={t.currency_code} className="bg-[#0d0d0d] font-semibold" data-testid={`global-revenue-total-${t.currency_code}`}>
                  <td className="px-5 py-2.5">Total {t.currency_code} <span className="text-[11px] font-normal text-zinc-500">({t.gyms} gym{t.gyms === 1 ? "" : "s"})</span></td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-zinc-400">{t.sales_count}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{money(t, t.total_revenue)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{money(t, t.net_revenue)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-amber-300">{money(t, t.pending)}</td>
                  <td className="px-5 py-2.5" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Programmes started (nutrition consult marked done) before they were paid enough and without the facility
// head's early-start approval (Anish, 23 Oct 2026). Each was emailed to the facility head, Stephanie and Anish.
// A flag clears itself once the consult is paid in full. Backend: routes/nutrition_start.py.
function StartedBeforePaying() {
  const [gym, setGym] = useState("");
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  useEffect(() => {
    listStartedUnpaid({ status: status === "all" ? undefined : status, facility_id: gym || undefined })
      .then(setData).catch(() => setData({ open: 0, items: [] }));
  }, [gym, status]);
  const [gyms, setGyms] = useState([]);
  useEffect(() => {
    listStartedUnpaid({ status: undefined }).then((d) => {
      const m = new Map((d.items || []).map((i) => [i.facility_id, i.gym]));
      setGyms([...m.entries()].map(([id, name]) => ({ id, name: name || id })));
    }).catch(() => {});
  }, []);
  const items = data?.items || [];
  const money = (cur, v) => `${cur || ""} ${Math.round(Number(v || 0)).toLocaleString()}`.trim();
  return (
    <section className="card-surface overflow-hidden" data-testid="started-before-paying">
      <div className="px-5 py-4 flex items-end justify-between gap-3 flex-wrap border-b border-[#1f1f1f]">
        <div>
          <div className="eyebrow">Started before paying</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={`font-display text-3xl font-extrabold tabular-nums ${data?.open ? "text-rose-300" : "text-zinc-500"}`}
              data-testid="started-unpaid-open">{data?.open ?? 0}</span>
            <span className="text-xs text-zinc-500">open</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 max-w-xl">Nutrition consult marked done (so the programme started) before full payment, with no early start approved. Clears when paid in full.</div>
        </div>
        <div className="flex gap-2">
          <select value={gym} onChange={(e) => setGym(e.target.value)} data-testid="started-unpaid-gym"
            className="bg-[#0a0a0a] border border-[#262626] rounded-md text-xs px-2 py-1.5">
            <option value="">All gyms</option>
            {gyms.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="started-unpaid-status"
            className="bg-[#0a0a0a] border border-[#262626] rounded-md text-xs px-2 py-1.5">
            <option value="open">Open</option>
            <option value="cleared">Cleared (paid)</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-zinc-500">Nothing here.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-5 py-2 text-left font-medium">Member</th>
                <th className="px-3 py-2 text-left font-medium">Gym</th>
                <th className="px-3 py-2 text-right font-medium">Paid</th>
                <th className="px-3 py-2 text-right font-medium">Owing</th>
                <th className="px-3 py-2 text-left font-medium">Started</th>
                <th className="px-3 py-2 text-left font-medium">Marked by</th>
                <th className="px-3 py-2 text-left font-medium">Email</th>
                <th className="px-5 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => {
                const failed = (f.emails || []).filter((e) => !e.sent);
                return (
                  <tr key={f.id} className="border-t border-[#161616]" data-testid={`started-unpaid-${f.id}`}>
                    <td className="px-5 py-2">
                      <div className="text-zinc-100">{f.member}</div>
                      <div className="text-[11px] text-zinc-500">{f.programme} · rep {f.sales_rep || "?"} · nutrition {f.nutrition_coach || "?"}</div>
                    </td>
                    <td className="px-3 py-2 text-zinc-300">{f.gym}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(f.currency_code, f.paid)} <span className="text-zinc-500">({f.paid_pct}%)</span></td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-300">{money(f.currency_code, f.owed)}</td>
                    <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{f.done_on}</td>
                    <td className="px-3 py-2 text-zinc-300">{f.marked_by}</td>
                    <td className="px-3 py-2 text-[11px]" title={(f.emails || []).map((e) => `${e.to}: ${e.sent ? "sent" : `not sent (${e.reason || "?"})`}`).join("\n")}>
                      {(f.emails || []).length === 0 ? <span className="text-zinc-500">none</span>
                        : failed.length ? <span className="text-amber-300">{failed.length} not sent</span>
                        : <span className="text-emerald-300">sent</span>}
                    </td>
                    <td className="px-5 py-2">
                      <span className={f.status === "open" ? "text-rose-300" : f.status === "cleared" ? "text-emerald-300" : "text-zinc-500"}>
                        {f.status === "open" ? "Open" : f.status === "cleared" ? "Paid" : "Withdrawn"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OverviewTab({ overview, loading, onGoto }) {
  if (loading || !overview) return <SkeletonBlock />;
  const t = overview.totals || {};
  const stats = [
    { label: "Facilities",     value: t.facilities,      hint: `${t.active_facilities} active` },
    { label: "Users",          value: t.users,           hint: `${t.pending_invites} pending invites` },
    { label: "Master Admins",  value: t.master_admins,   hint: "full cross-facility access" },
    { label: "Admins",         value: t.admins,          hint: "manage facilities & users" },
    { label: "Managers",       value: t.managers,        hint: "invite + reports" },
    { label: "Regular Users",  value: t.regular_users,   hint: "data-entry roles" },
  ];
  return (
    <div className="space-y-8">
      <RevenueByGym />
      <StartedBeforePaying />
      <section className="card-surface p-5 flex items-end justify-between gap-6 flex-wrap" data-testid="global-active-members">
        <div>
          <div className="eyebrow">Active members · all gyms</div>
          <div className="font-display text-3xl sm:text-5xl font-extrabold mt-2 text-emerald-300 tabular-nums">{t.active_members ?? 0}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Programme has started and the expiry date hasn&apos;t passed (paid-but-not-started and exited members excluded)</div>
        </div>
        {(t.expiry_unknown ?? 0) > 0 && (
          <div className="text-right" data-testid="global-expiry-unknown">
            <div className="font-display text-2xl font-extrabold text-amber-300 tabular-nums">{t.expiry_unknown}</div>
            <div className="text-[11px] text-zinc-500">members with no expiry on file<br />not counted until their sale has a programme</div>
          </div>
        )}
      </section>

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="global-overview-stats">
        {stats.map((s) => (
          <div key={s.label} className="card-surface p-4">
            <div className="eyebrow">{s.label}</div>
            <div className="font-display text-3xl font-extrabold mt-2">{s.value ?? 0}</div>
            <div className="text-[11px] text-zinc-500 mt-1">{s.hint}</div>
          </div>
        ))}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow">Per-facility snapshot</div>
          <button
            onClick={() => onGoto("facilities")}
            className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
            data-testid="overview-goto-facilities"
          >
            Manage facilities <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-5 py-3 font-medium">Facility</th>
                <th className="px-5 py-3 font-medium">Country</th>
                <th className="px-5 py-3 font-medium">Active members</th>
                <th className="px-5 py-3 font-medium" title="Paid, programme starts later">Not started</th>
                <th className="px-5 py-3 font-medium" title="Members whose expiry date has passed">Expired</th>
                <th className="px-5 py-3 font-medium" title="Members whose latest sales have no programme or expiry date">Expiry unknown</th>
                <th className="px-5 py-3 font-medium">Staff</th>
                <th className="px-5 py-3 font-medium">Sales</th>
                <th className="px-5 py-3 font-medium">Leads</th>
                <th className="px-5 py-3 font-medium">Members</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {(overview.facilities || []).map((f) => (
                <tr key={f.id} className="border-b border-[#1a1a1a] last:border-0" data-testid={`overview-fac-row-${f.id}`}>
                  <td className="px-5 py-3.5 font-medium">{f.name}</td>
                  <td className="px-5 py-3.5 text-zinc-400">
                    <span className="text-zinc-300">{f.country}</span>
                    <span className="ml-2 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 font-mono text-[10px]">{f.currency_code}</span>
                  </td>
                  <td className="px-5 py-3.5 font-semibold text-emerald-300" data-testid={`overview-active-members-${f.id}`}>{f.active_members ?? 0}</td>
                  <td className="px-5 py-3.5 text-zinc-400 tabular-nums">{f.not_started_members ?? 0}</td>
                  <td className="px-5 py-3.5 text-zinc-400 tabular-nums">{f.expired_members ?? 0}</td>
                  <td className={`px-5 py-3.5 tabular-nums ${(f.expiry_unknown ?? 0) > 0 ? "text-amber-300" : "text-zinc-500"}`}>{f.expiry_unknown ?? 0}</td>
                  <td className="px-5 py-3.5 text-zinc-400" title="Users with access to this facility">{f.members}</td>
                  <td className="px-5 py-3.5">{f.sales}</td>
                  <td className="px-5 py-3.5">{f.leads}</td>
                  <td className="px-5 py-3.5">{f.customers}</td>
                  <td className="px-5 py-3.5">
                    {f.active ? (
                      <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Active</Badge>
                    ) : (
                      <Badge className="bg-zinc-500/10 text-zinc-400 border border-zinc-500/30">Deactivated</Badge>
                    )}
                  </td>
                </tr>
              ))}
              {(overview.facilities || []).length === 0 && (
                <tr><td colSpan={10} className="px-5 py-10 text-center text-sm text-zinc-500">No facilities yet — create the first one from the Facilities tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <BackupsCard />
    </div>
  );
}

/* ------------------------------ Facilities ------------------------------ */
function FacilitiesTab({ overview, users, loading, onRefresh }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editFac, setEditFac] = useState(null);

  const facilities = overview?.facilities || [];

  const toggleActive = async (fac) => {
    if (fac.active) {
      if (!window.confirm(`Deactivate "${fac.name}"?\n\nUsers won't be able to switch into it. You can hard-delete it afterwards.`)) return;
      try {
        await deactivateFacility(fac.id);
        toast.success(`Deactivated ${fac.name}`);
        onRefresh();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not deactivate");
      }
    } else {
      // Re-activate via update
      try {
        await updateFacility(fac.id, { active: true });
        toast.success(`Reactivated ${fac.name}`);
        onRefresh();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not reactivate");
      }
    }
  };

  const hardDelete = async (fac) => {
    if (fac.active) return toast.error("Deactivate the facility before permanent deletion.");
    if (!window.confirm(`PERMANENTLY delete "${fac.name}"?\n\nThis removes memberships, catalogs and slabs. Sales/leads/customers with this facility_id are preserved but hidden. This cannot be undone.`)) return;
    try {
      await deleteFacilityPermanent(fac.id);
      toast.success(`${fac.name} permanently deleted`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <SkeletonBlock />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Facilities ({facilities.length})</div>
          <p className="text-xs text-zinc-500 mt-1">Create new gyms, edit settings, deactivate or hard-delete. All fields are cross-facility.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger data-testid="global-admin-dialog-trigger-285" asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black rounded-full px-5" data-testid="global-create-facility-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New facility
            </Button>
          </DialogTrigger>
          <CreateFacilityDialog
            existingUsers={users}
            onDone={() => { setCreateOpen(false); onRefresh(); }}
          />
        </Dialog>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-5 py-3 font-medium">Facility</th>
              <th className="px-5 py-3 font-medium">Country</th>
              <th className="px-5 py-3 font-medium">Currency</th>
              <th className="px-5 py-3 font-medium">Timezone</th>
              <th className="px-5 py-3 font-medium">Active members</th>
              <th className="px-5 py-3 font-medium">Staff</th>
              <th className="px-5 py-3 font-medium">Last updated</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {facilities.map((f) => (
              <tr key={f.id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors" data-testid={`global-fac-row-${f.id}`}>
                <td className="px-5 py-3.5">
                  <div className="font-medium">{f.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{f.id}</div>
                </td>
                <td className="px-5 py-3.5 text-zinc-300">{f.city ? `${f.city} · ` : ""}{f.country}</td>
                <td className="px-5 py-3.5 font-mono text-xs text-zinc-300">{f.currency_symbol} · {f.currency_code}</td>
                <td className="px-5 py-3.5 text-zinc-400 text-xs">{f.timezone}</td>
                <td className="px-5 py-3.5 font-semibold text-emerald-300" data-testid={`global-fac-active-members-${f.id}`}>{f.active_members ?? 0}</td>
                <td className="px-5 py-3.5" title="Users with access to this facility">{f.members}</td>
                <td className="px-5 py-3.5 text-zinc-400 text-xs">
                  {/* Prefer updated_at (set on every PATCH / tax edit / deactivate),
                      fall back to created_at for facilities that never had a subsequent edit. */}
                  <div title={f.updated_at || f.created_at || ""}>{fmtRelative(f.updated_at || f.created_at)}</div>
                  {(f.updated_at || f.created_at) && (
                    <div className="text-[10px] text-zinc-600 mt-0.5">{fmtDate(f.updated_at || f.created_at)}</div>
                  )}
                </td>
                <td className="px-5 py-3.5">
                  {f.active ? (
                    <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Active</Badge>
                  ) : (
                    <Badge className="bg-zinc-500/10 text-zinc-400 border border-zinc-500/30">Deactivated</Badge>
                  )}
                </td>
                <td className="px-5 py-3.5 text-right space-x-2">
                  <button
                    onClick={() => setEditFac(f)}
                    className="inline-flex items-center gap-1 text-[11px] text-blue-300 hover:text-blue-200 border border-blue-500/30 hover:border-blue-500/60 rounded-full px-2.5 py-1 hover:bg-blue-500/10 transition-colors"
                    data-testid={`global-edit-fac-${f.id}`}
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button
                    onClick={() => toggleActive(f)}
                    className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                      f.active
                        ? "text-amber-300 border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-500/60"
                        : "text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10 hover:border-emerald-500/60"
                    }`}
                    data-testid={`global-toggle-fac-${f.id}`}
                  >
                    {f.active ? <><PowerOff className="h-3 w-3" /> Deactivate</> : <><Power className="h-3 w-3" /> Reactivate</>}
                  </button>
                  {!f.active && (
                    <button
                      onClick={() => hardDelete(f)}
                      className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/30 hover:border-rose-500/60 rounded-full px-2.5 py-1 hover:bg-rose-500/10 transition-colors"
                      data-testid={`global-delete-fac-${f.id}`}
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {facilities.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-zinc-500">No facilities yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editFac && (
        <EditFacilityDialog
          fac={editFac}
          onClose={() => setEditFac(null)}
          onSaved={() => { setEditFac(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function CreateFacilityDialog({ existingUsers, onDone }) {
  const [name, setName] = useState("");
  const [preset, setPreset] = useState(COUNTRY_PRESETS[0]);
  const [country, setCountry] = useState(preset.country);
  const [city, setCity] = useState("");
  const [ghlLocationId, setGhlLocationId] = useState("");
  const [currencyCode, setCurrencyCode] = useState(preset.currency_code);
  const [currencySymbol, setCurrencySymbol] = useState(preset.currency_symbol);
  const [timezone, setTimezone] = useState(preset.timezone);
  const [adminMode, setAdminMode] = useState("none"); // "none" | "invite" | "existing"
  const [adminEmail, setAdminEmail] = useState("");
  const [adminUserId, setAdminUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const applyPreset = (countryName) => {
    const p = COUNTRY_PRESETS.find((c) => c.country === countryName);
    if (p) {
      setPreset(p);
      setCountry(p.country);
      setCurrencyCode(p.currency_code);
      setCurrencySymbol(p.currency_symbol);
      setTimezone(p.timezone);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Give the facility a name");
    if (!/^[A-Za-z0-9]{20}$/.test(ghlLocationId.trim())) return toast.error("Enter the gym's 20-character GoHighLevel location ID");
    setBusy(true);
    try {
      const body = {
        facility: {
          name: name.trim(),
          country,
          city: city.trim(),
          ghl_location_id: ghlLocationId.trim(),
          currency_code: currencyCode,
          currency_symbol: currencySymbol,
          timezone,
        },
      };
      if (adminMode === "invite" && adminEmail.trim()) {
        body.admin_email = adminEmail.trim().toLowerCase();
      } else if (adminMode === "existing" && adminUserId) {
        body.admin_user_id = adminUserId;
      }
      const res = await createFacilityWithAdmin(body);
      const adminMsg = res.admin
        ? (res.admin.kind === "invited" ? ` · invite sent to ${res.admin.email}` : ` · ${res.admin.email} attached as admin`)
        : "";
      toast.success(`Created ${res.facility.name}${adminMsg}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create facility");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="global-create-facility-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">New facility</DialogTitle>
        <DialogDescription className="text-xs text-zinc-500">Create a new gym across regions and optionally assign or invite its first admin in one step.</DialogDescription>
      </DialogHeader>
      <form data-testid="global-admin-form-447" onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="LIFTR Chennai · T Nagar"
            className="bg-[#0a0a0a] border-[#262626] mt-1.5"
            data-testid="global-fac-name"
            required
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">City</Label>
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Chennai"
            className="bg-[#0a0a0a] border-[#262626] mt-1.5"
            data-testid="global-fac-city"
          />
          <p className="text-[10px] text-zinc-600 mt-1">Used to auto-match city-wide events to this gym on the Events calendar.</p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">GoHighLevel location ID</Label>
          <Input
            value={ghlLocationId}
            onChange={(e) => setGhlLocationId(e.target.value)}
            placeholder="e.g. kg9psHvpnMAlx6FTMgv5"
            className="bg-[#0a0a0a] border-[#262626] mt-1.5 font-mono"
            data-testid="global-fac-ghl-location"
            required
          />
          <p className="text-[10px] text-zinc-600 mt-1">
            The gym&apos;s own GHL sub-account: the code after /location/ in its GHL address. Its WhatsApp and email go out through it.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Country preset</Label>
            <Select data-testid="global-admin-select-462" value={country} onValueChange={applyPreset}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="global-fac-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                {COUNTRY_PRESETS.map((c) => (
                  <SelectItem data-testid="global-admin-select-item-468" key={c.country} value={c.country} className="focus:bg-[#1a1a1a]">
                    {c.country} · {c.currency_code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Timezone</Label>
            <Input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="bg-[#0a0a0a] border-[#262626] mt-1.5"
              data-testid="global-fac-tz"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Currency code</Label>
            <Input
              value={currencyCode}
              onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
              className="bg-[#0a0a0a] border-[#262626] mt-1.5 font-mono"
              data-testid="global-fac-currency-code"
              maxLength={4}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Currency symbol</Label>
            <Input
              value={currencySymbol}
              onChange={(e) => setCurrencySymbol(e.target.value)}
              className="bg-[#0a0a0a] border-[#262626] mt-1.5 font-mono"
              data-testid="global-fac-currency-symbol"
              maxLength={4}
            />
          </div>
        </div>

        <div className="pt-2 border-t border-[#1f1f1f]">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="h-3.5 w-3.5 text-amber-400" />
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Initial admin (optional)</Label>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {[
              { key: "none", label: "Skip" },
              { key: "existing", label: "Pick existing user" },
              { key: "invite", label: "Invite by email" },
            ].map((opt) => (
              <button
                type="button"
                key={opt.key}
                onClick={() => setAdminMode(opt.key)}
                className={`rounded-lg border px-3 py-2 transition-colors ${
                  adminMode === opt.key
                    ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                    : "border-[#262626] text-zinc-400 hover:border-[#3a3a3a]"
                }`}
                data-testid={`global-fac-admin-mode-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {adminMode === "invite" && (
            <Input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="new.admin@yourgym.com"
              className="bg-[#0a0a0a] border-[#262626] mt-3"
              data-testid="global-fac-admin-email"
            />
          )}
          {adminMode === "existing" && (
            <div className="mt-3">
              <SearchableSelect data-testid="global-admin-searchable-select-544"
                value={adminUserId}
                onChange={setAdminUserId}
                options={(existingUsers || []).map((u) => ({
                  value: u.user_id,
                  label: u.name || u.email,
                  hint: u.email,
                }))}
                placeholder="Choose a user…"
                emptyLabel="— clear selection —"
                testid="global-fac-admin-user"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-black rounded-full" data-testid="global-fac-submit">
            {busy ? "Creating…" : "Create facility"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditFacilityDialog({ fac, onClose, onSaved }) {
  const [name, setName] = useState(fac.name || "");
  const [country, setCountry] = useState(fac.country || "");
  const [city, setCity] = useState(fac.city || "");
  const [currencyCode, setCurrencyCode] = useState(fac.currency_code || "");
  const [currencySymbol, setCurrencySymbol] = useState(fac.currency_symbol || "");
  const [timezone, setTimezone] = useState(fac.timezone || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateFacility(fac.id, {
        name: name.trim() || undefined,
        country: country || undefined,
        city: city.trim(),
        currency_code: currencyCode || undefined,
        currency_symbol: currencySymbol || undefined,
        timezone: timezone || undefined,
      });
      toast.success(`${name} updated`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="global-edit-facility-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Edit facility</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Rename, change country/currency preset, or update the timezone.</DialogDescription>
        </DialogHeader>
        <form data-testid="global-admin-form-605" onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</Label>
            <Input data-testid="global-admin-input-608" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0a0a0a] border-[#262626] mt-1.5" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Country</Label>
              <Input data-testid="global-admin-input-613" value={country} onChange={(e) => setCountry(e.target.value)} className="bg-[#0a0a0a] border-[#262626] mt-1.5" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">City</Label>
              <Input data-testid="global-edit-fac-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Chennai" className="bg-[#0a0a0a] border-[#262626] mt-1.5" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Timezone</Label>
              <Input data-testid="global-admin-input-617" value={timezone} onChange={(e) => setTimezone(e.target.value)} className="bg-[#0a0a0a] border-[#262626] mt-1.5" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Currency code</Label>
              <Input data-testid="global-admin-input-621" value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())} className="bg-[#0a0a0a] border-[#262626] mt-1.5 font-mono" maxLength={4} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Currency symbol</Label>
              <Input data-testid="global-admin-input-625" value={currencySymbol} onChange={(e) => setCurrencySymbol(e.target.value)} className="bg-[#0a0a0a] border-[#262626] mt-1.5 font-mono" maxLength={4} />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="global-edit-fac-submit">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------- Users --------------------------------- */
function UsersTab({ users, invites, facilities, currentUserId, currentRole, onRefresh }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [manageUser, setManageUser] = useState(null);
  const [filter, setFilter] = useState("");
  const [savingAccess, setSavingAccess] = useState({});
  const isMaster = currentRole === "master_admin";

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return (users || []).filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.facilities || []).some((f) => (f.facility_name || "").toLowerCase().includes(q))
    );
  }, [users, filter]);

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})?\n\nThey'll be signed out and detached from every facility.`)) return;
    try {
      await deleteUser(u.user_id);
      toast.success(`${u.name} removed`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove user");
    }
  };

  const changeRole = async (u, next) => {
    try {
      await updateUserRole(u.user_id, next);
      toast.success(`${u.name} is now ${ROLE_LABELS[next]}`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update role");
    }
  };

  const toggleAccess = async (u, kind, next) => {
    const key = `${u.user_id}:${kind}`;
    setSavingAccess((s) => ({ ...s, [key]: true }));
    try {
      if (kind === "qc") await setUserQcAccess(u.user_id, next);
      else if (kind === "accounts") await setUserAccountsAccess(u.user_id, next);
      else if (kind === "accounts_head") await setUserAccountsHead(u.user_id, next);
      else if (kind === "nurture_manager") await setUserNurtureManager(u.user_id, next);
      else await setUserLeadNurtureAccess(u.user_id, next);
      if (kind === "nurture_manager") toast.success(`${u.name} ${next ? "is now" : "is no longer"} the Lead Nurture manager`);
      else {
        const label = { qc: "QC", accounts: "Accounts", accounts_head: "Head of accounts" }[kind] || "Lead Nurture";
        toast.success(`${u.name}: ${label} access ${next ? "granted" : "revoked"}`);
      }
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update access");
    } finally {
      setSavingAccess((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">All users ({users.length})</div>
          <p className="text-xs text-zinc-500 mt-1">Search across every facility. Assign to multiple gyms, change role, or remove.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search name, email, or facility…"
            className="bg-[#0a0a0a] border-[#262626] w-64"
            data-testid="global-users-search"
          />
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger data-testid="global-admin-dialog-trigger-709" asChild>
              <Button className="bg-blue-600 hover:bg-blue-500 rounded-full px-5" data-testid="global-invite-user-btn">
                <UserPlus className="h-4 w-4 mr-1.5" /> Invite user
              </Button>
            </DialogTrigger>
            <GlobalInviteDialog facilities={facilities} onDone={() => { setInviteOpen(false); onRefresh(); }} />
          </Dialog>
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-5 py-3 font-medium">Person</th>
              <th className="px-5 py-3 font-medium">Role</th>
              <th className="px-5 py-3 font-medium">Cross-tenant teams</th>
              <th className="px-5 py-3 font-medium">Facilities</th>
              <th className="px-5 py-3 font-medium">Joined</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(filtered || []).map((u) => {
              const isMe = u.user_id === currentUserId;
              const qc = !!u.qc_access;
              const ln = !!u.lead_nurture_access;
              const acc = !!u.accounts_access;
              const head = !!u.accounts_head;
              return (
                <tr key={u.user_id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors" data-testid={`global-user-row-${u.user_id}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-medium">{u.name} {isMe && <span className="text-[10px] text-zinc-500 ml-1">(you)</span>}</div>
                    <div className="text-[11px] text-zinc-400">{u.email}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <Select data-testid="global-admin-select-743" value={u.role || "user"} onValueChange={(v) => changeRole(u, v)} disabled={isMe || !isMaster}>
                      <SelectTrigger className="bg-[#0a0a0a] border-[#262626] h-8 w-40" data-testid={`global-user-role-${u.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                        {["user", "manager", "admin", "master_admin"].map((r) => (
                          <SelectItem data-testid="global-admin-select-item-749" key={r} value={r} className="focus:bg-[#1a1a1a]">{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!isMaster && !isMe && <div className="text-[10px] text-zinc-600 mt-1">Master Admin only</div>}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-col gap-1.5">
                      <AccessPill
                        active={qc}
                        label="QC"
                        onToggle={() => toggleAccess(u, "qc", !qc)}
                        busy={!!savingAccess[`${u.user_id}:qc`]}
                        testid={`global-qc-toggle-${u.user_id}`}
                      />
                      <AccessPill
                        active={ln}
                        label="Lead Nurture · all gyms"
                        onToggle={() => toggleAccess(u, "lead_nurture", !ln)}
                        busy={!!savingAccess[`${u.user_id}:lead_nurture`]}
                        testid={`global-ln-toggle-${u.user_id}`}
                      />
                      <AccessPill
                        active={!!u.nurture_manager}
                        label="Lead Nurture manager"
                        onToggle={() => toggleAccess(u, "nurture_manager", !u.nurture_manager)}
                        busy={!!savingAccess[`${u.user_id}:nurture_manager`]}
                        testid={`global-ln-manager-toggle-${u.user_id}`}
                      />
                      <AccessPill
                        active={acc}
                        label="Accounts"
                        onToggle={() => toggleAccess(u, "accounts", !acc)}
                        busy={!!savingAccess[`${u.user_id}:accounts`]}
                        testid={`global-accounts-toggle-${u.user_id}`}
                      />
                      <AccessPill
                        active={head}
                        label="Head of accounts"
                        onToggle={() => toggleAccess(u, "accounts_head", !head)}
                        busy={!!savingAccess[`${u.user_id}:accounts_head`]}
                        testid={`global-accounts-head-toggle-${u.user_id}`}
                      />
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    {u.facilities?.length ? (
                      <div className="flex flex-wrap gap-1.5 max-w-md">
                        {(u.facilities || []).map((f) => (
                          <span key={f.facility_id} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-200">
                            {f.facility_name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[11px] text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3.5 text-right space-x-2">
                    {/* Admins and Master Admins can both attach/detach a user across facilities from
                        the Global Admin console. Only Master Admins can remove users entirely. */}
                    <button
                      onClick={() => setManageUser(u)}
                      className="inline-flex items-center gap-1 text-[11px] text-blue-300 hover:text-blue-200 border border-blue-500/30 hover:border-blue-500/60 rounded-full px-2.5 py-1 hover:bg-blue-500/10 transition-colors"
                      data-testid={`global-manage-user-${u.user_id}`}
                    >
                      <Link2 className="h-3 w-3" /> Manage facilities
                    </button>
                    {!isMe && isMaster && (
                      <button
                        onClick={() => removeUser(u)}
                        className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/30 hover:border-rose-500/60 rounded-full px-2.5 py-1 hover:bg-rose-500/10 transition-colors"
                        data-testid={`global-remove-user-${u.user_id}`}
                      >
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-zinc-500">No users match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <PendingInvitesSection
        invites={invites}
        facilities={facilities}
        onRefresh={onRefresh}
      />

      {manageUser && (
        <ManageUserFacilitiesDialog
          userDoc={manageUser}
          facilities={facilities}
          onClose={() => setManageUser(null)}
          onChanged={onRefresh}
        />
      )}
    </div>
  );
}

function PendingInvitesSection({ invites, facilities, onRefresh }) {
  const facMap = Object.fromEntries((facilities || []).map((f) => [f.id, f]));
  const revoke = async (email) => {
    if (!window.confirm(`Cancel invite for ${email}?`)) return;
    try {
      await cancelInvite(email);
      toast.success(`Invite cancelled for ${email}`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    }
  };
  return (
    <section data-testid="global-pending-invites">
      <div className="flex items-center justify-between mb-3 mt-8">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-amber-400" />
            Pending invites
            <span className="text-zinc-500 normal-case tracking-normal text-[11px] ml-1">({invites.length})</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Emails pre-authorized to sign in with Google. Their role & facility attach automatically on first sign-in.
          </p>
        </div>
      </div>
      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Role on join</th>
              <th className="px-5 py-3 font-medium">Facility</th>
              <th className="px-5 py-3 font-medium">Invited</th>
              <th className="px-5 py-3 font-medium">Invited by</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invites.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-xs text-zinc-500">
                  No pending invites. Use &quot;Invite user&quot; above to pre-authorize an email.
                </td>
              </tr>
            )}
            {(invites || []).map((inv) => {
              const fac = inv.facility_id ? facMap[inv.facility_id] : null;
              return (
                <tr key={inv.email} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors" data-testid={`global-invite-row-${inv.email}`}>
                  <td className="px-5 py-3.5 font-mono text-zinc-200">{inv.email}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] rounded-full px-2 py-0.5 border border-amber-500/30 bg-amber-500/5 text-amber-200">
                      {ROLE_LABELS[inv.role] || inv.role || "user"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-zinc-300 text-xs">
                    {fac ? (
                      <span>{fac.name}<span className="ml-2 text-zinc-500 font-mono text-[10px]">{fac.currency_code}</span></span>
                    ) : (
                      <span className="text-zinc-500 italic">Global / any facility</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-500 text-xs font-mono">
                    {inv.at ? new Date(inv.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs">{inv.invited_by_name || "—"}</td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => revoke(inv.email)}
                      className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/30 hover:border-rose-500/60 rounded-full px-2.5 py-1 hover:bg-rose-500/10 transition-colors"
                      data-testid={`global-cancel-invite-${inv.email}`}
                    >
                      <X className="h-3 w-3" /> Cancel
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


function GlobalInviteDialog({ facilities, onDone }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("user");
  const [facilityId, setFacilityId] = useState("__none__");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nameProblem(fullName)) return toast.error(`Full name: ${nameProblem(fullName)}`);
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const res = await inviteUser(
        email.trim().toLowerCase(),
        role,
        facilityId === "__none__" ? undefined : facilityId,
        undefined,
        autoFixName(fullName),
      );
      toast.success(res.existing ? "Existing user updated" : "Invite sent");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md" data-testid="global-invite-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Invite user</DialogTitle>
        <DialogDescription className="text-xs text-zinc-500">Pre-authorize an email and optionally attach them to a facility. They&apos;ll get the role on their first Google sign-in.</DialogDescription>
      </DialogHeader>
      <form data-testid="global-admin-form-953" onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Full name</Label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} onBlur={() => fullName && setFullName(autoFixName(fullName))}
            placeholder="First and last name" className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="global-invite-name" required />
          {fullName && nameProblem(fullName) && <p className="text-[11px] text-rose-400 mt-1">{nameProblem(fullName)}</p>}
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@yourgym.com" className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="global-invite-email" required />
          <p className="text-[11px] text-zinc-500 mt-1.5">They get the role below the first time they sign in with Google.</p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Role</Label>
          <Select data-testid="global-admin-select-961" value={role} onValueChange={setRole}>
            <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="global-invite-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
              {["user", "manager", "admin", "master_admin"].map((r) => (
                <SelectItem data-testid="global-admin-select-item-967" key={r} value={r} className="focus:bg-[#1a1a1a]">{ROLE_LABELS[r]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Attach to facility (optional)</Label>
          <Select data-testid="global-admin-select-974" value={facilityId} onValueChange={setFacilityId}>
            <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="global-invite-facility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-h-60">
              <SelectItem data-testid="global-admin-select-item-979" value="__none__" className="focus:bg-[#1a1a1a]">— none / global —</SelectItem>
              {(facilities || []).filter((f) => f.active).map((f) => (
                <SelectItem data-testid="global-admin-select-item-981" key={f.id} value={f.id} className="focus:bg-[#1a1a1a]">{f.name} · {f.currency_code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="global-invite-submit">
            {busy ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ManageUserFacilitiesDialog({ userDoc, facilities, onClose, onChanged }) {
  const [busy, setBusy] = useState(null); // facility_id being toggled, or "__bulk__" during multi-attach
  const [addRole, setAddRole] = useState("user");
  const [selected, setSelected] = useState(() => new Set()); // ticked facilities pending attach
  const attachedIds = new Set((userDoc.facilities || []).map((f) => f.facility_id));

  const toggleSelected = (facId) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(facId)) next.delete(facId); else next.add(facId);
      return next;
    });
  };

  const attachOne = async (fac) => {
    setBusy(fac.id);
    try {
      await attachUserToFacility(fac.id, userDoc.user_id, addRole);
      toast.success(`Attached to ${fac.name}`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attach failed");
    } finally {
      setBusy(null);
    }
  };

  const attachSelected = async () => {
    if (!selected.size) return;
    setBusy("__bulk__");
    let ok = 0;
    const failed = [];
    // Sequential so a partial-success case is easy to reason about; the whole roster is
    // typically < 10 facilities so latency stays imperceptible for admins.
    for (const facId of selected) {
      const fac = (facilities || []).find((f) => f.id === facId);
      if (!fac) continue;
      try {
        await attachUserToFacility(fac.id, userDoc.user_id, addRole);
        ok += 1;
      } catch (err) {
        failed.push(fac.name);
      }
    }
    if (ok) toast.success(`Attached ${userDoc.name} to ${ok} facility${ok === 1 ? "" : "ies"}`);
    if (failed.length) toast.error(`Failed for: ${failed.join(", ")}`);
    setSelected(new Set());
    setBusy(null);
    onChanged();
  };

  const detach = async (fac) => {
    if (!window.confirm(`Remove ${userDoc.name} from ${fac.facility_name}?`)) return;
    setBusy(fac.facility_id);
    try {
      await detachUserFromFacility(fac.facility_id, userDoc.user_id);
      toast.success(`Removed from ${fac.facility_name}`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Detach failed");
    } finally {
      setBusy(null);
    }
  };

  const available = (facilities || []).filter((f) => f.active && !attachedIds.has(f.id));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="global-manage-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Manage facilities · {userDoc.name}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Admins and Master Admins can attach this user to multiple gyms at once, or detach them from any they no longer need.</DialogDescription>
        </DialogHeader>
        <div className="text-[11px] text-zinc-500 mt-1">{userDoc.email} · <span className="text-zinc-300">{ROLE_LABELS[userDoc.role]}</span></div>

        <div className="mt-5 space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Attached ({(userDoc.facilities || []).length})</Label>
          {(userDoc.facilities || []).length === 0 && (
            <div className="text-[11px] text-zinc-500 py-3">Not attached to any facility yet.</div>
          )}
          <div className="space-y-1.5">
            {(userDoc.facilities || []).map((f) => (
              <div key={f.facility_id} className="flex items-center justify-between border border-[#262626] rounded-lg px-3 py-2 bg-[#0a0a0a]">
                <div>
                  <div className="text-sm font-medium">{f.facility_name}</div>
                  <div className="text-[10px] text-zinc-500">{f.facility_country} · {f.facility_currency} · per-facility role: <span className="text-zinc-300">{f.role}</span></div>
                </div>
                <button
                  disabled={busy === f.facility_id || busy === "__bulk__"}
                  onClick={() => detach(f)}
                  className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/30 hover:border-rose-500/60 rounded-full px-2.5 py-1 disabled:opacity-50"
                  data-testid={`global-detach-${userDoc.user_id}-${f.facility_id}`}
                >
                  <Unlink className="h-3 w-3" /> Detach
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[#1f1f1f] space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Add as</Label>
              <Select data-testid="global-admin-select-1101" value={addRole} onValueChange={setAddRole}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626] h-8 w-32 text-xs" data-testid="global-attach-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                  <SelectItem data-testid="global-admin-select-item-1106" value="user" className="focus:bg-[#1a1a1a]">User</SelectItem>
                  <SelectItem data-testid="global-admin-select-item-1107" value="manager" className="focus:bg-[#1a1a1a]">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selected.size > 0 && (
              <Button
                onClick={attachSelected}
                disabled={busy === "__bulk__"}
                className="bg-blue-600 hover:bg-blue-500 rounded-full h-8 text-xs px-4"
                data-testid="global-bulk-attach-btn"
              >
                <Link2 className="h-3 w-3 mr-1" />
                {busy === "__bulk__" ? "Attaching…" : `Attach to ${selected.size} facility${selected.size === 1 ? "" : "ies"}`}
              </Button>
            )}
          </div>
          {available.length === 0 && <div className="text-[11px] text-zinc-500">Attached to every active facility.</div>}
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {(available || []).map((f) => {
              const isTicked = selected.has(f.id);
              return (
                <div key={f.id} className={`flex items-center justify-between border rounded-lg px-3 py-2 transition-colors ${isTicked ? "border-blue-500/60 bg-blue-500/[0.06]" : "border-[#262626] bg-[#0a0a0a] hover:border-[#3a3a3a]"}`}>
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={isTicked}
                      onChange={() => toggleSelected(f.id)}
                      disabled={busy === "__bulk__"}
                      className="h-4 w-4 accent-blue-500"
                      data-testid={`global-attach-check-${userDoc.user_id}-${f.id}`}
                    />
                    <div>
                      <div className="text-sm font-medium">{f.name}</div>
                      <div className="text-[10px] text-zinc-500">{f.country} · {f.currency_code}</div>
                    </div>
                  </label>
                  <button
                    disabled={busy === f.id || busy === "__bulk__"}
                    onClick={() => attachOne(f)}
                    className="inline-flex items-center gap-1 text-[11px] text-blue-300 hover:text-blue-200 border border-blue-500/30 hover:border-blue-500/60 rounded-full px-2.5 py-1 disabled:opacity-50"
                    data-testid={`global-attach-${userDoc.user_id}-${f.id}`}
                    title="Attach immediately without ticking"
                  >
                    <Link2 className="h-3 w-3" /> Attach
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------- shared --------------------------------- */
function SkeletonBlock() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-16 rounded-lg bg-[#121212] border border-[#1f1f1f] animate-pulse" />
      ))}
    </div>
  );
}


function AccessPill({ active, label, onToggle, busy, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-1 rounded-full border transition-colors ${active
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
        : "border-[#2a2a2a] bg-[#0a0a0a] text-zinc-500 hover:border-zinc-500/40 hover:text-zinc-300"} disabled:opacity-50`}
      data-testid={testid}
      title={active ? `${label} team member (cross-gym access)` : `Grant ${label} cross-gym access`}
    >
      {active ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
      {label}
    </button>
  );
}
